import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { membrosApi, cargosApi, gruposFamiliaresApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Users,
  Download,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// Componente Avatar para exibir foto ou inicial
const Avatar = ({ fotoUrl, nome }) => {
  const [imageError, setImageError] = React.useState(false);

  if (!fotoUrl || imageError) {
    return (
      <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
        <span className="text-sky-600 font-semibold text-xs">
          {nome?.charAt(0)?.toUpperCase()}
        </span>
      </div>
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center overflow-hidden flex-shrink-0">
      <img
        src={fotoUrl}
        alt={nome}
        className="w-full h-full object-cover"
        onError={() => setImageError(true)}
      />
    </div>
  );
};

export const Membros = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [membros, setMembros] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, membro: null });
  const [familiaModal, setFamiliaModal] = useState({
    open: false,
    loading: false,
    data: null,
    error: null,
  });

  useEffect(() => {
    fetchCargos();
    fetchMembros();
  }, [search]);

  const fetchCargos = async () => {
    try {
      const response = await cargosApi.list({
        igreja_id: user?.igreja_id,
      });
      setCargos(response.data || []);
    } catch (error) {
      console.error('Error fetching cargos:', error);
    }
  };

  const getCargoNome = (membro) => {
    // Tenta pegar o nome do cargo de diferentes formas que a API pode retornar
    if (membro.cargo_nome) {
      return membro.cargo_nome;
    }
    if (membro.cargo?.nome) {
      return membro.cargo.nome;
    }
    if (membro.cargo_id) {
      const cargo = cargos.find(c => c.id === membro.cargo_id || c.id === parseInt(membro.cargo_id));
      return cargo?.nome || null;
    }
    // Fallback para o campo antigo cargo (string) caso ainda exista
    return membro.cargo || null;
  };

  const fetchMembros = async () => {
    try {
      const response = await membrosApi.list({
        igreja_id: user?.igreja_id,
        search: search || undefined,
        ativo: true,
      });
      setMembros(response.data || []);
    } catch (error) {
      console.error('Error fetching membros:', error);
      toast.error('Erro ao carregar membros');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await membrosApi.delete(deleteDialog.membro.id);
      toast.success('Membro inativado com sucesso');
      setDeleteDialog({ open: false, membro: null });
      fetchMembros();
    } catch (error) {
      toast.error('Erro ao inativar membro');
    }
  };

  const openFamiliaModal = async (grupoFamiliarId) => {
    setFamiliaModal({ open: true, loading: true, data: null, error: null });
    try {
      const response = await gruposFamiliaresApi.get(grupoFamiliarId);
      setFamiliaModal((prev) => ({ ...prev, loading: false, data: response.data }));
    } catch (err) {
      setFamiliaModal((prev) => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || 'Erro ao carregar grupo familiar',
      }));
      toast.error('Erro ao carregar grupo familiar');
    }
  };

  const handleDownloadPdf = async (membro) => {
    try {
      const response = await membrosApi.getPdf(membro.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ficha_${membro.codigo || 'membro'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="membros-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Membros</h1>
          <p className="page-subtitle">Gerencie os membros da sua igreja</p>
        </div>
        <Button
          onClick={() => navigate('/membros/novo')}
          className="btn-primary"
          data-testid="add-membro-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Membro
        </Button>
      </div>

      {/* Search */}
      <Card className="stat-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-field"
              data-testid="search-membros"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-sky-50">
              <Users className="w-5 h-5 text-sky-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total de Membros</p>
              <p className="text-2xl font-bold text-slate-900">{membros.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="stat-card overflow-hidden" data-testid="membros-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : membros.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-slate-300" />
              <h3 className="mt-4 text-lg font-medium text-slate-900">Nenhum membro encontrado</h3>
              <p className="mt-2 text-slate-500">Comece adicionando um novo membro à sua igreja.</p>
              <Button
                onClick={() => navigate('/membros/novo')}
                className="mt-4 btn-primary"
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Membro
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Código</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="hidden md:table-cell">Telefone</TableHead>
                    <TableHead className="hidden lg:table-cell">Cargo</TableHead>
                    <TableHead className="hidden lg:table-cell">Idade</TableHead>
                    <TableHead className="w-12 text-center">Família</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {membros.map((membro) => (
                    <TableRow key={membro.id} className="hover:bg-slate-50" data-testid={`membro-row-${membro.id}`}>
                      <TableCell className="font-medium text-sky-600">{membro.codigo}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar fotoUrl={membro.foto_url} nome={membro.nome} />
                          <div>
                            <p className="font-medium text-slate-900">{membro.nome}</p>
                            <p className="text-xs text-slate-500 hidden sm:block">{membro.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-slate-600">
                        {membro.telefone_whatsapp || membro.telefone_residencial || '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {getCargoNome(membro) ? (
                          <Badge variant="secondary" className="bg-sky-50 text-sky-700">
                            {getCargoNome(membro)}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-600">
                        {membro.idade ? `${membro.idade} anos` : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {membro.tem_grupo_familiar && membro.grupo_familiar_id ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                            onClick={() => openFamiliaModal(membro.grupo_familiar_id)}
                            title="Ver grupo familiar"
                            data-testid={`membro-familia-${membro.id}`}
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`membro-actions-${membro.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/membros/${membro.id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Visualizar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/membros/${membro.id}/editar`)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPdf(membro)}>
                              <Download className="mr-2 h-4 w-4" />
                              Baixar Ficha PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteDialog({ open: true, membro })}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Inativar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, membro: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar Membro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja inativar o membro "{deleteDialog.membro?.nome}"?
              Esta ação pode ser revertida posteriormente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Grupo Familiar */}
      <Dialog open={familiaModal.open} onOpenChange={(open) => !open && setFamiliaModal({ open: false, loading: false, data: null, error: null })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-sky-600" />
              Grupo Familiar
            </DialogTitle>
          </DialogHeader>
          {familiaModal.loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
            </div>
          )}
          {familiaModal.error && (
            <p className="text-sm text-red-600 py-4">{familiaModal.error}</p>
          )}
          {!familiaModal.loading && !familiaModal.error && familiaModal.data && (
            <div className="space-y-5">
              {/* Cabeça da família */}
              {familiaModal.data.cabeca && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Cabeça da família</p>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sky-50 border border-sky-100">
                    <Avatar fotoUrl={familiaModal.data.cabeca.foto_url} nome={familiaModal.data.cabeca.nome} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{familiaModal.data.cabeca.nome}</p>
                      <p className="text-xs text-slate-500">{familiaModal.data.cabeca.codigo}</p>
                      {familiaModal.data.cabeca.idade != null && (
                        <p className="text-xs text-slate-600">{familiaModal.data.cabeca.idade} anos</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Cônjuge */}
              {familiaModal.data.conjuge && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Cônjuge</p>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <Avatar fotoUrl={familiaModal.data.conjuge.foto_url} nome={familiaModal.data.conjuge.nome} />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{familiaModal.data.conjuge.nome}</p>
                      <p className="text-xs text-slate-500">{familiaModal.data.conjuge.codigo}</p>
                      {familiaModal.data.conjuge.idade != null && (
                        <p className="text-xs text-slate-600">{familiaModal.data.conjuge.idade} anos</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Filhos */}
              {familiaModal.data.filhos && familiaModal.data.filhos.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Filhos ({familiaModal.data.filhos.length})
                  </p>
                  <ul className="space-y-2">
                    {familiaModal.data.filhos.map((filho) => (
                      <li key={filho.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <Avatar fotoUrl={filho.foto_url} nome={filho.nome} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">{filho.nome}</p>
                          <p className="text-xs text-slate-500">{filho.codigo}</p>
                          {filho.idade != null && (
                            <p className="text-xs text-slate-600">{filho.idade} anos</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Pai / Mãe (quando existirem) */}
              {(familiaModal.data.pai || familiaModal.data.mae) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {familiaModal.data.pai && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Pai</p>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <Avatar fotoUrl={familiaModal.data.pai.foto_url} nome={familiaModal.data.pai.nome} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate text-sm">{familiaModal.data.pai.nome}</p>
                          <p className="text-xs text-slate-500">{familiaModal.data.pai.codigo}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {familiaModal.data.mae && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Mãe</p>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <Avatar fotoUrl={familiaModal.data.mae.foto_url} nome={familiaModal.data.mae.nome} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate text-sm">{familiaModal.data.mae.nome}</p>
                          <p className="text-xs text-slate-500">{familiaModal.data.mae.codigo}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Membros;
